import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import {
  Button,
  Heading,
  Text,
  TextInput,
  useForm
} from 'stubben-edge-unified-frontend-react-components';

interface Props {
  title: string;
  currentStepIndex: number;
  setCurrentStepIndex: (e: number) => void;
  setEmail: (e: string) => void;
  setMfaOption: (e: string) => void;
  setBearerToken: (e: string) => void;
  setTenants: (e: any) => void;
  setCompany: (e: any) => void;
}

const LoginStep: FC<Props> = ({
  title,
  currentStepIndex,
  setCurrentStepIndex,
  setEmail: setParentEmail,
  setMfaOption: setParentMfaOption,
  setBearerToken: setParentBearerToken,
  setTenants,
  setCompany
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get('uuid') as string;
  const { companyUuid } = useParams<{ companyUuid: string }>();

  const {
    formData,
    register,
    validate,
    errors: emailErrors,
    addError,
    clearErrors,
    getErrorMessage
  } = useForm({ email: '' });

  const { verifyUser, loading, errors } = useAuth();

  const handleSubmit = async () => {
    const { hasErrors } = validate();
    if (hasErrors) return;
    try {
      const response = await verifyUser(formData.email);
      setParentEmail(formData.email);
      setParentMfaOption(response.mfa_option || '');
      setParentBearerToken(response.token);

      // If MFA isn't enabled, check tenants
      if (response.tenants) {
        const company = response.tenants.find(
          (tenant: any) => tenant.company.uuid === uuid || companyUuid
        );

        if (company) {
          setCompany(company.company);
          setCurrentStepIndex(currentStepIndex + 3);
        } else if (response.tenants.length > 1) {
          setTenants(response.tenants);
          setCurrentStepIndex(currentStepIndex + 2);
        } else {
          setCompany(response.tenants[0].company);
          setCurrentStepIndex(currentStepIndex + 3);
        }
      } else {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  useEffect(() => {
    setParentEmail(formData.email);
  }, [formData.email, setParentEmail]);

  useEffect(() => {
    if (errors?.email?.length) {
      errors.email.forEach((err) => addError('email', err, true));
    }
  }, [errors]);

  const handleEmailBlur = (hasError?: boolean) => {
    if (hasError) {
      addError('email', true);
    } else {
      clearErrors('email');
    }
  };

  return (
    <div className="space-y-8">
      <Heading size="xs">{title}</Heading>
      <div className="space-y-16">
        <div className="space-y-4">
          <TextInput
            label="Email"
            type="email"
            {...register('email', { required: 'Must enter an email address' })}
            onBlur={handleEmailBlur}
            error={!!emailErrors['email']}
            helperText={getErrorMessage('email')}
          />
          <Button
            onClick={handleSubmit}
            disabled={!!emailErrors['email']}
            loading={loading}
            size="lg"
            fullWidth
          >
            {loading ? 'Loading...' : 'Continue'}
          </Button>
        </div>
        <div className="space-y-4 text-[#565E73]">
          <Text variant="secondary" size="sm">
            Trouble logging in? Email our support team{' '}
            <a href="mailto:support@stubbenedge.com">support@stubbenedge.com</a>
          </Text>
          <Text variant="secondary" size="xs">
            <a href="/cookie-policy" className="underline hover:text-primary" target="_blank">
              Cookie Policy
            </a>
          </Text>          
          <Text variant="secondary" size="xs">
            This system is restricted to authorised users only. Individuals who
            attempt unauthorised access will be prosecuted.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default LoginStep;
